import { PlacementData } from './../_models/tutor-reports/tutorreport';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewChildren } from '@angular/core';
import { Router } from "@angular/router";
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
import { MatDialog } from '@angular/material/dialog';
import { StudentBooking } from '../_models/student-bookings/student-booking';
import { DropDownListElement } from '../shared/utility/drop-down-list-element';
import { PlacementMaintenance } from '../_models/maintenance/placement-maintenance.model';
import { DialogData, SessionDatePickerModalComponent } from '../tutor-reports/tutor-reports.sessions/session-date-picker-modal/session-date-picker-modal.component';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { StudentBookingStudentDetailsComponent } from './student-booking-student-details.component';
import { LearnerJourneyHelperService } from '../_helpers/learner-journey-helper/learner-journey-helper.service';
import { EditLearningSectionModalComponent } from '../tutor-reports/tutor-report-timeline/modals/edit-learning-section-modal/edit-learning-section-modal.component';
import { StudentBookingStudentDetails } from '../_models/student-bookings/student-booking-student-details';
import { SuccessModalHelperService } from '../_helpers/success-modal-helper/success-modal-helper.service';
import { StudentBookingsService } from '../_services/student-bookings/student-bookings.service';
import { SubjectsService } from '../_services/shared/subjects.service';
import { MergeLessonSummaryRequest } from '../_models/student-bookings/merge-lesson-summary-request.model';
import { TimelineSectionStudentInfo } from '../_models/timeline-models/timeline-section-student-info.model';
import { SelectedSessionDate } from '../_models/student-bookings/selected-session-date.model';
import { PlacementService } from '../_services';
import { Location } from '@angular/common';
import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { DynamicConfirmModalComponent } from '../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';

@Component({
    selector: 'student-bookings',
    templateUrl: 'student-bookings.component.html',
    styleUrls: ['student-bookings.component.css'],
    standalone: false
})
export class StudentBookingsComponent implements OnInit {
  @Input() placementId: number;
  @Input() selectedSessionDate: SelectedSessionDate;
  @Input() componentViewMode: string = 'View';
  @Output() onDataChanged = new EventEmitter<boolean>();

  @ViewChild('bookingForm', { static: false }) bookingForm: any;
  @ViewChild('lessonSummary', { static: false }) lessonSummary: ElementRef;
  @ViewChildren('studentDetails') studentSections: StudentBookingStudentDetailsComponent[];

  public studentBooking: StudentBooking;
  public bookingStudents: StudentBookingStudentDetails[];
  public subjects: DropDownListElement[] = [];
  public lockStudents: boolean = false;
  public studentCommentsMandatory: boolean = false;
  public studentIds: number[] = [];

  private placementMaintenance: PlacementMaintenance;
  private placementData: PlacementData;
  private sessionInfo: MergeLessonSummaryRequest;

  constructor(
    private router: Router,
    private placementService: PlacementService,
    public featureHelperService: FeatureHelperService,
    public dialog: MatDialog,
    private readonly subjectService: SubjectsService,
    private errorModalHelper: ErrorModalHelperService,
    private readonly learnerJourneyHelperService: LearnerJourneyHelperService,
    private readonly successModalHelperService: SuccessModalHelperService,
    private readonly studentBookingsService: StudentBookingsService,
    private location: Location,
    public themeService: ThemeService
  ) {
    let featureList = new Array<string>();
    featureList.push("TutorReportsView");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "TutorReportsView")[0].enabled) {
      console.error("User does not have th required permissions to view the learner journey session screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname } });
    }

    subjectService.getSubjectList().subscribe(data => {
      this.subjects = data;
    });

    this.componentViewMode = 'View';
    if (this.router.getCurrentNavigation() != null
      && this.router.getCurrentNavigation()?.extras != null
      && this.router.getCurrentNavigation()?.extras?.state != null) {

      const insertedState = this.router.getCurrentNavigation().extras.state;

      this.placementId = insertedState.placementId;
      this.selectedSessionDate = insertedState.selectedSessionDate;
      this.componentViewMode = insertedState.componentViewMode;

    }
    else if (this.learnerJourneyHelperService.studentBookingData) {

      this.studentBooking = this.learnerJourneyHelperService.getStudentBookingData();
      this.postLoadOperations();
    }
  }

  ngOnInit(): void {
    if (!this.placementId) {
      // this means we weren't routed nor injected here
      this.router.navigate(["/"]);
    } else {
      this.initializeScreen();
    }
  }

  setupForm() {
    if (this.studentBooking.lockSubject || !this.isFormEditable()) {
      this.bookingForm.form.controls['subject'].disable();
    } else {
      this.bookingForm.form.controls['subject'].enable();
    }
  }

  /* Form Control members */

  isFormEditable(): boolean {
    switch (this.studentBooking.status) {
      case 'New':
      case 'Saved':
      case 'Rejected':
        return this.featureHelperService.hasFeature('IndividualLearningReportsSave') ||
          this.featureHelperService.hasFeature('IndividualLearningReportsEdit');
      default:
        return false;
    };
  }

  setFormEditable(): void {
    if (!this.isFormEditable()) {
      this.bookingForm.form.disable();
    } else {
      this.bookingForm.form.enable();
    }
    this.setupForm();
  }

  saveSubmitButtonEnabled(): boolean {
    if (this.bookingStudents && this.bookingStudents.filter(f => f.studentId != 0).length > 0) {
      if (this.featureHelperService.hasFeature('IndividualLearningReportsSave')
        && !this.featureHelperService.hasFeature('TutorReportsSave')) {
        return this.studentBooking.status === 'New'
          || this.studentBooking.status === 'Saved'
          || this.studentBooking.status === 'Rejected';
      } if (this.featureHelperService.hasFeature('TutorReportsSave')) {
        return this.studentBooking.status === 'New'
          || this.studentBooking.status === 'Saved'
          || this.studentBooking.status === 'Submitted'
          || this.studentBooking.status === 'Rejected';
      }
    }
    return false;
  }

  approveButtonVisible(): boolean {
    return this.studentBooking.status === 'Submitted'
      && this.featureHelperService.hasFeature('IndividualLearningReportsApprove');
  }

  rejectButtonVisible(): boolean {
    return this.studentBooking.status === 'Submitted'
      && this.featureHelperService.hasFeature('IndividualLearningReportsReject');
  }

  deleteButtonVisible(): boolean {
    return this.studentBooking.status === 'Saved'
      || this.studentBooking.status === 'Rejected';
  }

  unlockButtonVisible(): boolean {
    return this.studentBooking.status === 'Approved'
      && this.featureHelperService.hasFeature('UnlockIndividualLearningReports');
  }

  newSessionDateButtonEnabled(): boolean {
    return (this.studentBooking.status === 'New'
      || this.studentBooking.status === 'Saved'
      || this.studentBooking.status === 'Rejected') && this.isFormEditable();
  }

  saveClicked(): void {
    this.unsetAbsenceReasonIfNotRequired();
    let oldStatus = this.studentBooking.status;
    this.setStatus('Saved');
    this.studentBooking.studentDetails = this.bookingStudents.filter(f => f.studentId != 0);
    this.studentBookingsService.mergeStudentBooking(this.studentBooking).subscribe((data: MergeLessonSummaryRequest) => {
      this.studentBooking.sessionId = data.sessionId;
      this.setStatus(data.statusDescription);
      this.studentBooking.studentDetails.forEach(student => {
        let studentMatched = data.studentDetails.find(s => s.studentId == student.studentId);
        if (studentMatched) {
          student.id = studentMatched.id;
        }
      });
      this.postSaveOperations('Save successful!');
    }, error => {
      console.error(error);
      this.errorModalHelper.show(error.message, error);
      this.setStatus(oldStatus);
    });
  }

  submitClicked(): void {
    if (this.validate()) {
      this.unsetAbsenceReasonIfNotRequired();
      let oldStatus = this.studentBooking.status;
      this.setStatus('Submitted');
      this.studentBooking.studentDetails = this.bookingStudents.filter(f => f.studentId != 0);
      this.studentBookingsService.mergeStudentBooking(this.studentBooking).subscribe((data: MergeLessonSummaryRequest) => {
        this.studentBooking.sessionId = data.sessionId;
        this.setStatus(data.statusDescription);
        this.studentBooking.studentDetails.forEach(student => {
          let studentMatched = data.studentDetails.find(s => s.studentId == student.studentId);
          if (studentMatched) {
            student.id = studentMatched.id;
          }

        });
        this.postSaveOperations('Submit successful!');
      }, error => {
        console.error(error);
        this.errorModalHelper.show(error.message, error);
        this.setStatus(oldStatus);
      });
    }
  }

  approveClicked(): void {
    if (this.validate()) {
      this.unsetAbsenceReasonIfNotRequired();
      let oldStatus = this.studentBooking.status;
      this.setStatus('Approved');
      this.studentBooking.studentDetails = this.bookingStudents.filter(f => f.studentId != 0);
      this.studentBookingsService.mergeStudentBooking(this.studentBooking).subscribe(data => {
        this.setStatus(data.statusDescription);
        this.postSaveOperations('Approve successful!');
      }, error => {
        console.error(error);
        this.errorModalHelper.show(error.message, error);
        this.setStatus(oldStatus);
      });
    }
  }

  unlockClicked(): void {
    let oldStatus = this.studentBooking.status;
    this.setStatus('Unlock');
    this.studentBooking.studentDetails = this.bookingStudents.filter(f => f.studentId != 0);
    this.studentBookingsService.mergeStudentBooking(this.studentBooking).subscribe(data => {
      this.setStatus(data.statusDescription);
      this.postSaveOperations('Unlock successful!');
    }, error => {
      console.error(error);
      this.errorModalHelper.show(error.message, error);
      this.setStatus(oldStatus);
    });
  }

  rejectClicked(): void {
    const dialogRef = this.dialog.open(DynamicConfirmModalComponent, {
      data: { confirmMessage: "This action will permanently remove the session, are you sure you wish to continue?" },
      panelClass: 'my-confirm-dialog'
    });
    dialogRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        let oldStatus = this.studentBooking.status;
        this.setStatus('Rejected');
        this.studentBooking.studentDetails = this.bookingStudents.filter(f => f.studentId != 0);
        this.studentBookingsService.mergeStudentBooking(this.studentBooking).subscribe(data => {
          this.setStatus(data.statusDescription);
          this.postSaveOperations('Rejected successfully!');
        }, error => {
          console.error(error);
          this.errorModalHelper.show(error.message, error);
          this.setStatus(oldStatus);
        });
      }
    });
  }

  deleteClicked(): void {
    let oldStatus = this.studentBooking.status;
    this.setStatus('Delete');
    this.studentBooking.studentDetails = this.bookingStudents.filter(f => f.studentId != 0);
    this.studentBookingsService.mergeStudentBooking(this.studentBooking).subscribe(data => {
      window.scrollTo(0,0);
      this.successModalHelperService.show('Deleted successfully!');
      this.onDataChanged.emit(true);
    }, error => {
      console.error(error);
      this.errorModalHelper.show(error.message, error);
      this.setStatus(oldStatus);
    });
  }

  newSessionDateClicked(): void {
    const dialogRef = this.dialog.open(SessionDatePickerModalComponent, {
      data: {
        startDate: this.studentBooking.startDate,
        startTime: this.studentBooking.startTime,
        endTime: this.studentBooking.endTime,
        timeLocked: this.placementData.lockTime,
        method: "Update"
      },
      panelClass: 'my-dialog'
    });
    dialogRef.afterClosed().subscribe((result: DialogData) => {
      if (result.completed) {
        this.studentBooking.startDate = result.startDate;
        this.studentBooking.startTime = result.startTime;
        this.studentBooking.endTime = result.endTime;
        this.filterStudentList(result.startDate);
      }
    });
  }

  validate(): boolean {
    let valid = true;
    if (this.bookingForm.invalid) {
      this.errorModalHelper.show("Please complete all sections before submitting", { isValidationError: true });
      valid = false;
    }
    if (this.studentCommentsMandatory && (!this.studentBooking.lessonSummary || this.studentBooking.lessonSummary.length < 100)) {      
        this.errorModalHelper.show("Please complete all sections before submitting", { isValidationError: true });
        valid = false;                 
    }

    this.studentSections.forEach(section => {
      if (section.studentForm.invalid) {
        this.errorModalHelper.show("Please complete all sections before submitting", { isValidationError: true });
        valid = false;
      }
      //should probably use form mechanisms to validate this but formFields inside child components sucks
      if (section.model.comprehension <= 0 && section.model.studentId != 0 && (section.model.attendance == 'Present' || section.model.attendance == 'Late')) {
        this.errorModalHelper.show("Please complete all sections before submitting", { isValidationError: true });
        valid = false;
      }
      if (section.model.engagement <= 0 && section.model.studentId != 0 && (section.model.attendance == 'Present' || section.model.attendance == 'Late')) {
        this.errorModalHelper.show("Please complete all sections before submitting", { isValidationError: true });
        valid = false;
      }
      if (this.studentCommentsMandatory && (section.model.attendance == 'Present' || section.model.attendance == 'Late')) {        
        if (!section.model.learnerComments || section.model.learnerComments.length < 100) {
          this.errorModalHelper.show("Please complete all sections before submitting", { isValidationError: true });
          valid = false;
        }        
      }
      if (section.model.attendance == 'Absent' || section.model.attendance == 'Late') {        
        if (!section.model.reasonDescription || section.model.reasonDescription.length < 4) {
          this.errorModalHelper.show("Please complete all sections before submitting", { isValidationError: true });
          valid = false;
        }        
      }
    });
    return valid;
  }

  private unsetAbsenceReasonIfNotRequired(): void {
    this.studentSections.forEach(section => {
      if (section.model.attendance != 'Absent' && section.model.attendance != 'Late') {
        section.model.reasonDescription = "";
      }
    });
  }

  /* Form Control members */

  addGroupLearningSection() {
    if (this.bookingStudents.filter(f => f.studentId != 0).length > 0) {
      const dialogRef = this.dialog.open(EditLearningSectionModalComponent, {
        data: {
          editMode: true,
          timelineSection: null,
          add: true,
          students: this.bookingStudents.filter(f => f.studentId != 0).map(s => new TimelineSectionStudentInfo(s.studentId, s.studentFullName)),
          subject: this.studentBooking.subject,
          placementId: this.placementId,
        },
        panelClass: 'my-timeline-edit-dialog'
      });
      dialogRef.afterClosed().subscribe((reload: boolean) => {
        if (reload) {
          this.onDataChanged.emit(true);
        }
      });
    } else {
      this.errorModalHelper.show("Please assign " + this.themeService.siteContent.learnerPupilLabel.toLowerCase() + "(s) before adding a learning section.", { isValidationError: true });
    }
  }

  public studentModelUpdated(student: StudentBookingStudentDetails, i: number): void {
    this.studentBooking[i] = student;
  }

  public backClick() {
    this.location.back();
  }

  private setStatus(status: string): void {
    this.studentBooking.status = status;
    //this.setFormEditable();
  }

  private initializeScreen(): void {

    const methodArray = [this.readPlacementMaintenance(), this.readPlacementData()];
    if (this.componentViewMode != 'Add') {
      methodArray.push(this.readLessonSummary());
    }

    Promise.all(methodArray).then(() => {
      // Default student booking info
      this.studentBooking = new StudentBooking(
        this.placementMaintenance,
        this.selectedSessionDate,
        this.placementData);

      if (this.sessionInfo) {
        // set api values found for lesson summary
        this.studentBookingApiResponseMapper(this.sessionInfo)
      }

      this.lockStudents = !this.placementMaintenance.placementInfo.studentsSwappable;
      this.studentCommentsMandatory = !this.placementMaintenance.placementInfo.studentCommentsOptional;
      this.postLoadOperations();

      let filteredStudents = this.bookingStudents.filter(f => f.studentId != 0);
      if (filteredStudents.length > 0) {
        this.studentBookingsService.retrieveStudentProgression(filteredStudents.map(s => s.studentId), this.studentBooking.subject, this.studentBooking.placementId).subscribe(data => {
          data.forEach(d => {
            let student = this.studentBooking.studentDetails.find(s => {
              return s.studentId === d.studentId;
            });
            student.latestLearningSection = { lastAssessmentScore: d.assessmentScore, lastProgress: d.currentProgress, lastTarget: d.lastTarget };
          });
        });
      }


      setTimeout(() => {
        this.setFormEditable();
        this.setupForm();
        this.lessonSummary.nativeElement.required = this.studentCommentsMandatory;
      });
    }).catch(error => {
      let errorMessage = error.status === 401 ? "Unauthorized" : error.message;
      if (errorMessage == "Object reference not set to an instance of an object.") {
        errorMessage = "Client not found. Try another."
      }
      this.errorModalHelper.show(errorMessage, error);

    });

  }

  private postLoadOperations(): void {
    this.learnerJourneyHelperService.setStudentBookingData(this.studentBooking);
    this.filterStudentList(this.studentBooking.startDate);
  }

  private postSaveOperations(message: string): void {
    this.successModalHelperService.show(message);
    this.setFormEditable();
  }

  private filterStudentList(sessionDate: Date): void {
    this.bookingStudents = new Array<StudentBookingStudentDetails>();
    this.studentIds = [];
    this.studentBooking.studentDetails.forEach((studentItem: StudentBookingStudentDetails, index: number) => {
      if (sessionDate == null
        || (studentItem.sessionsEnd == null && studentItem.sessionsStart == null)
        || ((studentItem.sessionsEnd == null || (studentItem.sessionsEnd != null && new Date(studentItem.sessionsEnd).setHours(0, 0, 0, 0) > new Date(sessionDate).setHours(0, 0, 0, 0)))
          && (studentItem.sessionsStart == null || (studentItem.sessionsStart != null && new Date(sessionDate).setHours(0, 0, 0, 0) >= new Date(studentItem.sessionsStart).setHours(0, 0, 0, 0))))
      ) {
        this.bookingStudents.push(studentItem);
      }
    });

    if (this.placementMaintenance && (this.bookingStudents.length < this.placementMaintenance.placementInfo.groupSize)) {
      let difference = this.placementMaintenance.placementInfo.groupSize - this.bookingStudents.length;
      for (let index = 0; index < difference; index++) {
        this.bookingStudents.push(new StudentBookingStudentDetails());
      }
    }

    this.studentIds.push(...this.bookingStudents.filter(s => s.studentId != 0).map(m => m.studentId));
  }

  private studentBookingApiResponseMapper(sessionInfo: MergeLessonSummaryRequest) {
    this.studentBooking.sessionId = sessionInfo.sessionId;
    this.studentBooking.tutorId = sessionInfo.tutorId;
    this.studentBooking.placementId = sessionInfo.placementId;
    this.studentBooking.subject = sessionInfo.subject;
    this.studentBooking.lessonSummary = sessionInfo.lessonSummary;
    this.studentBooking.lessonType = sessionInfo.lessonType;
    this.studentBooking.startDate = sessionInfo.startDate;
    this.studentBooking.startTime = sessionInfo.startTime;
    this.studentBooking.endTime = sessionInfo.endTime;
    this.studentBooking.status = sessionInfo.statusDescription;

    let newStudents = new Array<StudentBookingStudentDetails>();
    sessionInfo.studentDetails.forEach(student => {
      let bookingStudent = new StudentBookingStudentDetails();
      bookingStudent.id = student.id;
      bookingStudent.studentId = student.studentId;
      bookingStudent.studentFullName = student.studentName;
      bookingStudent.attendance = student.attendance;
      bookingStudent.engagement = student.engagement != null ? student.engagement : 0;
      bookingStudent.comprehension = student.comprehension != null ? student.comprehension : 0;
      bookingStudent.learnerComments = student.learnerComments;
      bookingStudent.reasonDescription = student.reasonDescription;

      if (this.placementMaintenance && this.placementMaintenance.placementStudents) {
        let placementStudent = this.placementMaintenance.placementStudents.find(ps => ps.studentId == student.studentId);
        if (placementStudent) {
          bookingStudent.sessionsStart = placementStudent.sessionsStart;
          bookingStudent.sessionsEnd = placementStudent.sessionsEnd;
        }
      }

      bookingStudent.latestLearningSection = {
        lastProgress: student.studentProgression.currentProgress,
        lastTarget: student.studentProgression.lastTarget,
        lastAssessmentScore: student.studentProgression.assessmentScore
      };
      newStudents.push(bookingStudent);
    });

    this.studentBooking.studentDetails = newStudents;
  }

  public studentIdChanged($event): void {
    // this.filterStudentList(this.studentBooking.startDate);
    this.studentIds = [];
    this.studentIds.push(...this.bookingStudents.filter(s => s.studentId != 0).map(m => m.studentId));
  }

  private readPlacementMaintenance(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.placementService.getPlacementMaintenanceInfo(this.placementId.toString()).subscribe((response: PlacementMaintenance) => {
        this.placementMaintenance = response;
        resolve(true);
      }, error => {
        reject(error);
      })
    });
  }

  private readPlacementData(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.placementService.getById(this.placementId).subscribe((response: PlacementData) => {
        this.placementData = response;
        resolve(true);
      }, error => {
        reject(error);
      })
    });
  }

  private readLessonSummary(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.studentBookingsService.getStudentBooking(this.placementId, this.selectedSessionDate.startDate, this.selectedSessionDate.startTime).subscribe((sessionInfo: MergeLessonSummaryRequest) => {
        this.sessionInfo = sessionInfo;
        resolve(true);
      }, error => {
        reject(error);
      })
    })
  }
}
